/**
 * As opções dos selects de categoria e subcategoria financeiras de um item.
 *
 * Papel: aqui ficam `opcoesDeCategoria` e `opcoesDeSubcategoria`, que dizem o
 * que o cartão "Financeiro — Item N" de `FormularioPedido` oferece a partir da
 * `ClassificacaoFinanceira` que a API devolve, e as frases com que o formulário
 * e o quadro recusam registrar a compra de item sem categoria.
 *
 * Como usar: as duas acrescentam ao fim o valor gravado que o plano não tem — a
 * categoria de um item antigo, a que alguém excluiu do plano depois. Um
 * `<select>` cujo valor não está entre as opções mostra a primeira delas, "—
 * Selecionar —", e o item pareceria sem categoria embora a tenha; ao salvar, o
 * valor continuaria gravado sem que ninguém o visse.
 */
import { type ClassificacaoFinanceira } from '@concierge/shared';

/** O que o campo de categoria de um item pendente mostra abaixo dele. */
export const CAMPO_SEM_CATEGORIA = 'Selecione a categoria financeira.';

/** O que a caixa de compra do formulário mostra quando algum item não tem categoria. */
export const AVISO_DA_COMPRA_SEM_CATEGORIA =
  'Selecione a categoria financeira de cada item antes de registrar a compra — necessária para lançar a despesa no Financeiro.';

/**
 * O que a caixa de compra do quadro mostra quando algum pedido da decisão tem
 * item sem categoria.
 *
 * @param numeros - O número de cada pedido pendente, na ordem da seleção.
 * @returns A frase, terminada pelos números, para quem vai abrir cada um.
 */
export function avisoDaCompraSemCategoriaNoQuadro(numeros: readonly string[]): string {
  return `Abra o(s) pedido(s) e selecione a categoria financeira de cada item antes de registrar a compra — necessária para lançar a despesa no Financeiro. Pendente(s): ${numeros.join(', ')}`;
}

/** As categorias de despesa, como `ClassificacaoFinanceira` as traz. */
type CategoriasDoPlano = ClassificacaoFinanceira['categorias'];

/**
 * Os nomes acrescidos do valor gravado, quando ele não está entre eles.
 *
 * @param nomes - As opções que o plano oferece.
 * @param atual - O valor do campo; vazio não acrescenta nada.
 * @returns As opções, com o valor gravado no fim quando faltava.
 */
function comOGravado(nomes: readonly string[], atual: string): string[] {
  return atual === '' || nomes.includes(atual) ? [...nomes] : [...nomes, atual];
}

/**
 * As categorias que o select de um item oferece.
 *
 * @param categorias - As categorias de despesa, na ordem em que a API as manda.
 * @param atual - A categoria do item, ou a string vazia.
 * @returns Os nomes, com a categoria gravada no fim quando o plano não a tem.
 */
export function opcoesDeCategoria(categorias: CategoriasDoPlano, atual: string): string[] {
  return comOGravado(
    categorias.map((categoria) => categoria.nome),
    atual,
  );
}

/**
 * As subcategorias que o select de um item oferece, as da categoria escolhida.
 *
 * @param categorias - As categorias de despesa, com as subcategorias em ordem.
 * @param categoria - A categoria escolhida no item; vazia, não há o que listar.
 * @param atual - A subcategoria do item, ou a string vazia.
 * @returns Os nomes, com a subcategoria gravada no fim quando a categoria não a
 *   tem.
 */
export function opcoesDeSubcategoria(
  categorias: CategoriasDoPlano,
  categoria: string,
  atual: string,
): string[] {
  const doPlano = categorias.find((daLista) => daLista.nome === categoria);
  return comOGravado(doPlano?.subcategorias ?? [], atual);
}
